/**
 * SupplierInvoicesList — laedt Lieferanten-Rechnungen und rendert sie als Liste.
 * Sortierung: Overdue zuerst, dann Open (nach DueOn), Disputed, Paid (nach PaidOn DESC).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { I } from '@/shared/icons';
import { Button } from '@/shared/components';
import { toApiError, useApi, type SupplierInvoiceResponse } from '@/shared/api';
import { SupplierInvoiceRow } from './SupplierInvoiceRow';

interface Props {
  propertyId: string;
  onDownload: (propertyFileId: string) => void;
  onPreview?: (propertyFileId: string) => void;
  onEdit?: (invoice: SupplierInvoiceResponse) => void;
  onUploadHere: () => void;
  displayMode?: 'list' | 'preview';
  lookupFile?: (fileId: string) => import('@/shared/api').PropertyFileResponse | null;
}

export function SupplierInvoicesList({
  propertyId,
  onDownload,
  onPreview,
  onEdit,
  onUploadHere,
  displayMode = 'list',
  lookupFile,
}: Props) {
  const api = useApi();
  const qc = useQueryClient();

  const invoices = useQuery({
    queryKey: ['supplierInvoices', propertyId],
    queryFn: () => api.supplierInvoices.list(propertyId),
  });

  const markPaid = useMutation({
    mutationFn: (id: string) =>
      api.supplierInvoices.markPaid(propertyId, id, {
        paidOn: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => {
      toast.success('Als bezahlt markiert');
      void qc.invalidateQueries({ queryKey: ['supplierInvoices', propertyId] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  const markDisputed = useMutation({
    mutationFn: (id: string) => api.supplierInvoices.markDisputed(propertyId, id),
    onSuccess: () => {
      toast.success('Als strittig markiert');
      void qc.invalidateQueries({ queryKey: ['supplierInvoices', propertyId] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.supplierInvoices.delete(propertyId, id),
    onSuccess: () => {
      toast.success('Rechnung gelöscht');
      void qc.invalidateQueries({ queryKey: ['supplierInvoices', propertyId] });
      void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  if (invoices.isLoading) {
    return <p className="text-ink-faint text-xs">Lade Rechnungen…</p>;
  }
  if (invoices.error || !invoices.data) {
    return <p className="text-sealwax text-xs">Rechnungen konnten nicht geladen werden.</p>;
  }

  const sorted = sortInvoices(invoices.data);
  if (sorted.length === 0) {
    return <EmptyHint onUpload={onUploadHere} />;
  }

  return (
    <ul className="space-y-0.5">
      {sorted.map((inv) => (
        <SupplierInvoiceRow
          key={inv.id}
          invoice={inv}
          onDownload={onDownload}
          onPreview={onPreview}
          onEdit={onEdit}
          onMarkPaid={(x) => markPaid.mutate(x.id)}
          onMarkDisputed={(x) => markDisputed.mutate(x.id)}
          onDelete={(x) => remove.mutate(x.id)}
          propertyId={propertyId}
          displayMode={displayMode}
          file={lookupFile ? lookupFile(inv.propertyFileId) : null}
        />
      ))}
    </ul>
  );
}

function sortInvoices(items: SupplierInvoiceResponse[]): SupplierInvoiceResponse[] {
  const rank = (i: SupplierInvoiceResponse): number => {
    if (i.status === 'Overdue') return 0;
    if (i.status === 'Disputed') return 1;
    if (i.status === 'Open') return 2;
    return 3;
  };
  return [...items].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    const aDate = a.paidOn ?? a.dueOn ?? a.issuedOn;
    const bDate = b.paidOn ?? b.dueOn ?? b.issuedOn;
    return bDate.localeCompare(aDate);
  });
}

function EmptyHint({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="border-line/60 rounded-md border border-dashed px-4 py-8 text-center">
      <p className="text-ink-soft mb-3 text-sm">
        Noch keine Lieferantenrechnung hinterlegt. Strom, Gas, Wasser, Internet und Reparaturen
        gehören hierher.
      </p>
      <Button variant="brand" leftIcon={<I name="Plus" size={12} />} onClick={onUpload}>
        Rechnungen hochladen
      </Button>
    </div>
  );
}
